import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { ErrorDisplay } from "@cohesive/ui-components";
import { LiveblocksError } from "@liveblocks/client";
import { useErrorListener } from "@liveblocks/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getRoomConnectionErrorCopy } from "./canvasRoomConnectionErrorCopy";

type ConnectionErrorState = {
  code: number;
  detail: string;
} | null;

export function CanvasRoomConnectionErrors({
  roomId,
  showOverlay,
}: {
  roomId: string;
  /** When false (e.g. capture iframe), listener no-ops and no overlay is shown. */
  showOverlay: boolean;
}) {
  const navigate = useNavigate();
  const [connectionError, setConnectionError] =
    useState<ConnectionErrorState>(null);

  useEffect(() => {
    setConnectionError(null);
  }, [roomId]);

  useErrorListener((err) => {
    if (!showOverlay) {
      return;
    }
    if (!(err instanceof LiveblocksError)) {
      return;
    }
    const ctx = err.context;
    if (ctx.type !== "ROOM_CONNECTION_ERROR") {
      return;
    }
    if (ctx.roomId !== roomId) {
      return;
    }
    setConnectionError({
      code: ctx.code,
      detail: err.message,
    });
  });

  if (!showOverlay || !connectionError) {
    return null;
  }

  const copy = getRoomConnectionErrorCopy(connectionError.code);
  const message = connectionError.detail
    ? `${copy.description} (${connectionError.detail})`
    : copy.description;

  return (
    <div
      className="fixed inset-0 z-[200] overflow-y-auto bg-background/95"
      role="alert"
    >
      <ErrorDisplay
        title={copy.title}
        message={message}
        footer={
          <div className="flex flex-wrap items-center justify-center gap-2">
            {connectionError.code === 4001 ? (
              <Button
                type="button"
                onClick={() => navigate(ROUTES.WORKSPACES.ROOT)}
              >
                Back to workspaces
              </Button>
            ) : null}
            <Button
              type="button"
              variant={connectionError.code === 4001 ? "outline" : "default"}
              onClick={() => window.location.reload()}
            >
              Refresh page
            </Button>
          </div>
        }
      />
    </div>
  );
}
